import { Input, InputProps } from 'antd';
import { MeasureType } from 'domains/measurementDomain';
import { useMeasureField, useMeasurementUnit } from 'hooks/useMeasurementUnit';

interface Props extends Omit<InputProps, 'value' | 'onChange' | 'suffix'> {
  /** The value as the API returned it, already in the unit the viewer is working in. */
  value?: number | string | null;
  /** Called with the number the user typed, verbatim - the API converts, not this. */
  onChange?: (value: number | undefined) => void;
  /** Which quantity this field holds, so the right unit and conversion are used. */
  type?: MeasureType;
  /** Set false where the row already labels the unit once for a `W x L` pair. */
  showSuffix?: boolean;
}

/**
 * The text-input flavour of {@link RevxMeasureInput}, for the admin forms that use antd `Input`.
 *
 * Same contract: values arrive and leave in the viewer's unit and nothing is converted here,
 * an untouched field submits the value it loaded unchanged, and an edited one converts back at full
 * precision rather than display precision.
 */
export const RevxMeasureTextInput = ({
  value,
  onChange,
  type = MeasureType.LENGTH,
  showSuffix = true,
  ...rest
}: Props) => {
  // The spelled unit rather than the tight symbol: this sits in a field suffix, where `in` says
  // what `"` only implies, and it is what tells the reader which fields the form's unit toggle
  // governs. `8.5 in` also reads unambiguously beside a caliper's `µm`.
  const { abbrev } = useMeasurementUnit();
  const field = useMeasureField(type, { value, onChange });

  return (
    <Input
      {...rest}
      value={field.value ?? ''}
      suffix={showSuffix ? abbrev(type) : undefined}
      onFocus={(e) => {
        field.onFocus();
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        field.onBlur();
        rest.onBlur?.(e);
      }}
      onChange={(e) => {
        const next = e.target.value;
        field.onChange(next === '' ? null : next);
      }}
    />
  );
};
